'use client';

import React, { useEffect, useState } from 'react';
import Title from '@/components/layout/title';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { TutorialBlock } from '@/types/exchanges/page';
import Iphone15Pro from '@/components/ui/iphone-15-pro';
import { getImageProps } from 'next/image';

interface TutorialsProps {
    tutorials?: TutorialBlock[];
    title?: string | undefined;
    contenttype?: string | undefined;
    tutorialTitle?: string | undefined;
}

/** The screenshot through the image optimizer at 2x its ~200px display width (828px), not the
 * original upload. No `sizes` on purpose: with it, `src` would be the largest (3840px) size. */
const screenSrc = (src: string) =>
    getImageProps({ src, alt: '', width: 390, height: 844 }).props.src;

const Tutorials = ({ tutorials, title, contenttype, tutorialTitle }: TutorialsProps) => {
    // The section (titles and steps) is in the server HTML; it used to render nothing until the
    // browser mounted it, so crawlers missed it and it popped in late. Only the screenshots wait
    // for the browser: an SVG <image> cannot lazy-load and would compete with the page's main
    // image.
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    return (
        <section className='my-8 border-b pb-3 dark:border-white/20'>
            <Title
                title={`آموزش تصویری ${tutorialTitle ? tutorialTitle : `${contenttype} ${title}`}`}
                classes='text-center'
            />
            <Tabs defaultValue='tab-0' dir='rtl'>
                <ScrollArea className='whitespace-nowrap w-full pb-4' dir='rtl'>
                    <TabsList className='bg-gray-100/70 dark:bg-primary-active lg:gap-x-2 gap-x-1 rounded-xl h-fit p-1 w-fit justify-start'>
                        {tutorials?.map((tab, index) => (
                            <TabsTrigger
                                key={index}
                                value={`tab-${index}`}
                                aria-label={`active tab ${tab.tabTitle}`}
                                className={`lg:px-4 px-3 py-2 transition rounded-xl text-primary dark:text-white/70 dark:data-[state=active]:text-primary data-[state=active]:bg-white max-lg:text-xs`}
                            >
                                {tab.tabTitle}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                    <ScrollBar orientation='horizontal' />
                </ScrollArea>

                {/* Tabs Content */}
                {tutorials?.map((tab, index) => (
                    <TabsContent key={index} value={`tab-${index}`}>
                        <ScrollArea className='whitespace-nowrap w-full pb-4' dir='rtl'>
                            <div key={index} className={`flex w-max gap-x-3`}>
                                {/* Show only 5 items per tab */}
                                {tab.tabContent?.map((content, index) => (
                                    <div
                                        key={index}
                                        className='w-[60vw] md:w-[20vw] lg:w-[15vw] flex flex-col gap-y-5 justify-start items-start p-1'
                                    >
                                        <Iphone15Pro
                                            className={
                                                'object-cover w-full h-auto aspect-[1/2] mx-auto '
                                            }
                                            src={
                                                isMounted
                                                    ? screenSrc(
                                                          content.image?.sourceUrl || '/logo.png',
                                                      )
                                                    : undefined
                                            }
                                        />
                                        <div className='grid lg:px-1'>
                                            <div className='text-sm font-semibold flex items-end text-wrap text-black dark:text-white'>
                                                <span>{index + 1}</span> _
                                                <span className={'mr-2'}>{content.title}</span>
                                            </div>
                                            <p className='text-xs text-wrap leading-5 text-primary/70 dark:text-white/70 mt-2'>
                                                {content.text}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <ScrollBar orientation='horizontal' />
                        </ScrollArea>
                    </TabsContent>
                ))}
            </Tabs>
        </section>
    );
};

export default Tutorials;
